/**
 * What a meal on a plan can be called — the list the builder's meal dialog
 * picks from.
 *
 * Backed by TanStack Query rather than fetched fresh with every opening of
 * the dialog: the list barely changes, so a workspace that has opened it once
 * this session gets it back instantly on every later opening, and only a
 * quiet background read checks whether it has moved.
 */

import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { fetchMealSlotTypes } from "../services/mealPlansService";
import type { MealSlotTypeOption } from "../types/plans.types";

export type MealSlotTypesStatus = "loading" | "ready" | "failed";

export interface MealSlotTypesState {
  options: MealSlotTypeOption[];
  status: MealSlotTypesStatus;
}

const NO_TYPES: MealSlotTypeOption[] = [];

export const MEAL_SLOT_TYPES_QUERY_KEY = ["plans", "meal-slot-types"] as const;

export function useMealSlotTypes(): MealSlotTypesState {
  const getToken = useTokenGetter();

  const query = useQuery({
    queryKey: MEAL_SLOT_TYPES_QUERY_KEY,
    queryFn: ({ signal }) => fetchMealSlotTypes(getToken, signal),
  });

  useEffect(() => {
    if (query.isError) console.error("Meal names failed to load:", query.error);
  }, [query.isError, query.error]);

  return {
    options: query.data ?? NO_TYPES,
    status:
      query.status === "pending"
        ? "loading"
        : query.status === "error"
        ? "failed"
        : "ready",
  };
}
